import { WallDatumAbstractionProvider } from "building-generation/elements/datums/datumAbstractionProviders";
import { buildSolidWallCrossSectionOutlines } from "building-generation/elements/walls/solidWallCrossSectionUtils";
import { wallCrossSection } from "building-generation/elements/walls/wall";
import { getWallPath } from "building-generation/elements/walls/wall";
import { wallsCrossSectionFaces } from "building-generation/elements/walls/wallCrossSectionFaces";
import { wallMassLayers } from "building-generation/elements/walls/wallLayers";
import {
  wallSideCrossSectionFinishMasses,
  wallSideFinishMassLayerCrossSections,
} from "building-generation/elements/walls/wallSide";
import { wallSideNew } from "building-generation/elements/walls/wallSideBasics";
import type { Building } from "building-types/building/buildingTypes";
import type { CompositeLevelHandle, LevelHandle } from "building-types/handles/docEntityHandleTypes";
import type { LineStyle } from "building-types/views/Primitive2DOutput";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolygonGeometryNew, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import { WallMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import type { WallSideCrossSection } from "building-types/walls/wallSideTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { Wall } from "building-types/walls/wallTypes";
import { FLOOR_PLAN_CUT_PLANE } from "common/constants/buildingObjects";
import colors from "common/constants/colorsOld";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { levelInchesNew, percentNew } from "common/utils/nominalNumbers";
import { isCompositeLevelContentSourceId, resolveContentSourceIdToLevelIds } from "doc/docViewport";
import { WALL_CUT_STROKE_WIDTH } from "doc/plans/constants";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { wallExteriorFinishMaskInlineStyle } from "doc/plans/planStyleSheets";
import { isHatchMaterialPaletteSlot } from "doc/utils/wallMaterialTools";
import { ZERO_EPSILON } from "geom/core";
import { HatchMaterialId } from "geom/hatchMaterialId";
import type { HatchMaterial } from "geom/hatchTypes";
import { hatchProceduralMaterialNew } from "geom/hatchTypes";
import { polyLine2PolyLine2DoIntersect } from "geom/intersect";
import { joinPolyLine2Loops } from "geom/join";
import type { OBB2 } from "geom/obb2";
import { obb2FromPathAndPolyLines } from "geom/obb2";
import { polygonUnion } from "geom/polygon";
import { polyLine2Area, polyLine2ByTwoPoints, polyLine2Close, polyLine2Copy, polyLine2SetCCW } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { getReportingUnitRoomIds } from "../reportingUnitRooms";
import { makeSheetGeometryForWindowWallCrossSections } from "./planWindows";

function resolveLevelIdsWithWalls(building: Building, levelId: string): string[] {
  const memberIds = resolveContentSourceIdToLevelIds(building.doc, levelId, building.levels);
  return memberIds.filter((id) => building.walls[id]);
}

function resolveLevelHandleFromBuilding(
  building: Building,
  levelId: string,
): LevelHandle | CompositeLevelHandle | undefined {
  if (isCompositeLevelContentSourceId(building.doc, levelId)) {
    return { type: "COMPOSITE_LEVEL", id: levelId };
  }
  return building.levels[levelId]?.handle;
}

export function mergeCompositeWallLoops(building: Building, levelId: string, loops: PolyLine2[]): PolyLine2[] {
  if (!isCompositeLevelContentSourceId(building.doc, levelId)) {
    return loops;
  }

  if (loops.length === 0) {
    return loops;
  }

  try {
    const unioned = polygonUnion(loops.map((loop) => [loop]))
      .map((union) => union[0])
      .filter((loop): loop is PolyLine2 => !!loop && polyLine2Area(loop) > ZERO_EPSILON);

    // Loops that are completely on the interior of the unioned set need to be included in the output.
    const nonIntersectingLoops = loops.filter(
      (loopPolyLine) =>
        !unioned.some((unionedPolyLine) => polyLine2PolyLine2DoIntersect(loopPolyLine, unionedPolyLine)),
    );

    return nonIntersectingLoops.concat(unioned);
  } catch (error) {
    console.error("Failed to merge composite wall loops, falling back to unmerged wall data", error);
    return loops;
  }
}

interface PlanWallProps {
  building: Building;
  levelId: string;
  selectedReportingUnitLabelId?: string;
  cutPlaneHeight?: LevelInches;
  wallFinishCutPlaneHeight?: LevelInches;
  crossSectionLineStyleOverride?: LineStyle;
  boundary?: boolean;
  core?: boolean;
  substrate?: boolean;
  exteriorFinish?: boolean;
}

function wallTouchesReportingUnitRooms(
  wall: Wall,
  reportingUnitRoomIds: Set<string> | undefined,
  wallPlanesEnabled: boolean,
): boolean {
  if (!reportingUnitRoomIds) {
    return true;
  }

  // Known limitation: in walls v2 (wall planes) a single wall can span multiple rooms vertically,
  // so its path room refs can't reliably attribute it to one reporting unit. Wall geometry is
  // emitted with a level handle (not a room/entity handle), so the selector layer can't filter it
  // either. Walls v2 walls are therefore always kept, i.e. not scoped to the selected unit.
  // wallPlanes is opt-in (default off), so only projects that enable it are affected.
  if (wallPlanesEnabled) {
    return true;
  }

  const wallPath = wall.wallType === WallType.SOLID ? wall.mass.path : wall.path;
  const leftRoomId = wallPath?.leftRoom?.id;
  const leftSourceRoomId = wallPath?.leftRoom?.sourceRoomId;
  if (
    (leftRoomId && reportingUnitRoomIds.has(leftRoomId)) ||
    (leftSourceRoomId && reportingUnitRoomIds.has(leftSourceRoomId))
  ) {
    return true;
  }

  const rightRoomId = wallPath?.rightRoom?.id;
  const rightSourceRoomId = wallPath?.rightRoom?.sourceRoomId;
  if (
    (rightRoomId && reportingUnitRoomIds.has(rightRoomId)) ||
    (rightSourceRoomId && reportingUnitRoomIds.has(rightSourceRoomId))
  ) {
    return true;
  }

  return false;
}

export function makeSheetGeometryForWallPlaneMasses(context: PlanGenerationContext) {
  if (context.renderStyleMode === "showroomFloorPlan") {
    return makeSheetGeometryForShowroomWallPlaneMasses(context);
  }

  return makeSheetGeometryFromWallStructureInternal({
    ...context,
    core: true,
  });
}

function makeSheetGeometryForShowroomWallPlaneMasses({
  building,
  levelId,
  cutPlaneHeight = levelInchesNew(FLOOR_PLAN_CUT_PLANE),
}: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const handle = resolveLevelHandleFromBuilding(building, levelId);

  for (const memberLevelId of resolveLevelIdsWithWalls(building, levelId)) {
    const level = building.levels[memberLevelId];
    const levelWalls = building.walls[memberLevelId];
    const planarGraph = building.planarGraphs[memberLevelId]?.planarGraph;
    if (!level || !levelWalls || !planarGraph) {
      continue;
    }

    const wallFaces = wallsCrossSectionFaces(
      planarGraph,
      levelWalls,
      level.floorElevation + cutPlaneHeight,
      WallLayerType.STRUCTURE,
      false,
      true,
      true,
      !building.enabledFeatures.wallPlanes,
    );

    for (const face of wallFaces) {
      const closedLoops = face.loops
        .map((loop) => polyLine2Close(polyLine2Copy(loop)))
        .filter((loop) => Math.abs(polyLine2Area(loop)) > ZERO_EPSILON);

      if (closedLoops.length === 0) {
        continue;
      }

      result.push(
        sheetPolygonGeometryNew({
          handle,
          paths: [closedLoops],
        }),
      );
    }
  }

  return result;
}

export function makeSheetGeometryForWallExteriorFinish(context: PlanGenerationContext) {
  return makeSheetGeometryFromWallStructureInternal({
    ...context,
    cutPlaneHeight: context.wallFinishCutPlaneHeight ?? context.cutPlaneHeight,
    exteriorFinish: true,
  });
}

export function makeSheetGeometryForWallExteriorFinishProjected({
  building,
  levelId,
  cutPlaneHeight,
  wallFinishCutPlaneHeight,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);
  return makeSheetGeometryForWallFinishCrossSections(
    building,
    levelId,
    true,
    wallFinishCutPlaneHeight ?? cutPlaneHeight,
    reportingUnitRoomIds,
  );
}

export function makeSheetGeometryForWallBoundaries(context: PlanGenerationContext) {
  return makeSheetGeometryFromWallStructureInternal({
    ...context,
    boundary: true,
  });
}

function makeSheetGeometryFromWallStructureInternal({
  building,
  levelId,
  selectedReportingUnitLabelId,
  cutPlaneHeight,
  wallFinishCutPlaneHeight,
  boundary = false,
  core = false,
  substrate = false,
  exteriorFinish = false,
}: PlanWallProps) {
  const result: SheetGeometry[] = [];
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  if (core) {
    result.push(
      ...makeSheetGeometryForWallCrossSections({
        building,
        levelId,
        cutPlaneHeight,
        wallLayer: WallLayerType.STRUCTURE,
        reportingUnitRoomIds,
      }),
    );
  }
  if (substrate) {
    result.push(
      ...makeSheetGeometryForWallCrossSections({
        building,
        levelId,
        cutPlaneHeight,
        wallLayer: WallLayerType.SUBSTRATE,
        reportingUnitRoomIds,
      }),
    );
  }
  if (exteriorFinish) {
    result.push(
      ...makeSheetGeometryForWallFinishCrossSections(building, levelId, false, cutPlaneHeight, reportingUnitRoomIds),
    );
  }
  if (boundary) {
    result.push(
      ...makeSheetGeometryForWallFullCrossSections(
        building,
        levelId,
        cutPlaneHeight,
        wallFinishCutPlaneHeight,
        reportingUnitRoomIds,
      ),
    );
  }
  // Some windows generate their own plan sections of walls.
  // TODO - deprecate and remove this
  // Window wall cross sections need real level IDs (they access building.levels[levelId]),
  // so resolve composite levels to their members.
  for (const memberLevelId of resolveLevelIdsWithWalls(building, levelId)) {
    result.push(
      ...makeSheetGeometryForWindowWallCrossSections({
        building,
        levelId: memberLevelId,
        cutPlaneHeight,
        selectedReportingUnitLabelId,
      }),
    );
  }

  return result;
}

export function makeSheetGeometryFromWallSubstrate({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: PlanWallProps) {
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);
  return makeSheetGeometryForWallCrossSections({
    building,
    levelId,
    cutPlaneHeight,
    wallLayer: WallLayerType.SUBSTRATE,
    reportingUnitRoomIds,
  });
}

interface PlanWallCrossSectionProps {
  building: Building;
  levelId: string;
  cutPlaneHeight?: LevelInches;
  wallLayer?: WallLayerType;
  reportingUnitRoomIds?: Set<string>;
}

function makeSheetGeometryForWallFullCrossSections(
  building: Building,
  levelId: string,
  cutPlaneHeight?: LevelInches,
  wallFinishCutPlaneHeight?: LevelInches,
  reportingUnitRoomIds?: Set<string>,
) {
  const result: SheetGeometry[] = [];
  const memberLevelIds = resolveLevelIdsWithWalls(building, levelId);
  const finishCutPlane = wallFinishCutPlaneHeight ?? cutPlaneHeight;

  const crossSections: PolyLine2[] = [
    ...makeWallCrossSections(building, levelId, WallLayerType.STRUCTURE, cutPlaneHeight, reportingUnitRoomIds)
      .crossSections,
    ...makeWallCrossSections(building, levelId, WallLayerType.SUBSTRATE, cutPlaneHeight, reportingUnitRoomIds)
      .crossSections,
    ...memberLevelIds.flatMap((mlId) =>
      makeWallFinishCrossSections(building, mlId, finishCutPlane, reportingUnitRoomIds).flatMap((x) => x.crossSection),
    ),
  ];

  const handle = resolveLevelHandleFromBuilding(building, levelId);

  for (const union of polygonUnion(crossSections.map((c) => [c]))) {
    if (!union[0]) {
      continue;
    }
    if (polyLine2Area(union[0]) > ZERO_EPSILON) {
      result.push(
        sheetPolylineGeometryNew({
          handle,
          paths: union,
        }),
      );
    }
  }

  return result;
}

function makeWallFinishCrossSections(
  building: Building,
  levelId: string,
  cutPlaneHeight?: LevelInches,
  reportingUnitRoomIds?: Set<string>,
) {
  const { settings, datumAbstractions, levels, levelsAttributes } = building;
  const { wallMaterials } = settings.wallCatalog;
  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);
  const wallFinishCrossSections: WallSideCrossSection[] = [];

  const walls = building.walls[levelId].walls;
  const wallPlanesEnabled = !!building.enabledFeatures.wallPlanes;
  for (const wallId in walls) {
    const wall = walls[wallId];
    if (!wallTouchesReportingUnitRooms(wall, reportingUnitRoomIds, wallPlanesEnabled)) {
      continue;
    }

    let leftSide = wallSideNew();
    let rightSide = wallSideNew();
    if (wall.wallType === WallType.SOLID) {
      leftSide = wall.leftSide;
      rightSide = wall.rightSide;
    }

    const leftFinishMasses = wallSideCrossSectionFinishMasses(
      wallDatumAbstractionProvider,
      wallMaterials,
      wall,
      true,
      cutPlaneHeight,
    );
    const rightFinishMasses = wallSideCrossSectionFinishMasses(
      wallDatumAbstractionProvider,
      wallMaterials,
      wall,
      false,
      cutPlaneHeight,
    );

    for (const finishSideMass of leftFinishMasses) {
      if (finishSideMass.wallType === WallType.SOLID_MASS) {
        const finishCrossSections = wallSideFinishMassLayerCrossSections(
          leftSide,
          rightSide,
          finishSideMass,
          wallMaterials,
          true,
          cutPlaneHeight,
        );
        wallFinishCrossSections.push(...finishCrossSections);
      }
    }

    for (const finishSideMass of rightFinishMasses) {
      if (finishSideMass.wallType === WallType.SOLID_MASS) {
        const finishCrossSections = wallSideFinishMassLayerCrossSections(
          leftSide,
          rightSide,
          finishSideMass,
          wallMaterials,
          false,
          cutPlaneHeight,
        );
        wallFinishCrossSections.push(...finishCrossSections);
      }
    }
  }

  return wallFinishCrossSections;
}

/**
 * @NOTE: this is very similar to lib/components/studio/editorViewer/renderEditorViewerWallAssemblies.ts
 */
function makeSheetGeometryForWallFinishCrossSections(
  building: Building,
  levelId: string,
  projected: boolean,
  cutPlaneHeight?: LevelInches,
  reportingUnitRoomIds?: Set<string>,
) {
  const result: SheetGeometry[] = [];
  const memberLevelIds = resolveLevelIdsWithWalls(building, levelId);

  const crossSections: PolyLine2[] = [];
  const crossSectionsByFinish: { [type: string]: PolyLine2[] } = {};

  for (const memberLevelId of memberLevelIds) {
    const wallFinishCrossSections = projected
      ? makeWallFinishCrossSectionsProjected(building, memberLevelId, cutPlaneHeight, reportingUnitRoomIds)
      : makeWallFinishCrossSections(building, memberLevelId, cutPlaneHeight, reportingUnitRoomIds);

    for (const finishCrossSection of wallFinishCrossSections) {
      if (finishCrossSection.layer.type === WallMaterialType.EXTERIOR_FINISH) {
        const key = finishCrossSection.layer.finishMaterial;
        if (!crossSectionsByFinish[key]) {
          crossSectionsByFinish[key] = [];
        }
        crossSectionsByFinish[key].push(...finishCrossSection.crossSection);
      } else {
        crossSections.push(...joinPolyLine2Loops(finishCrossSection.crossSection));
      }
    }
  }

  const handle = resolveLevelHandleFromBuilding(building, levelId);

  for (const key in crossSectionsByFinish) {
    const loops = crossSectionsByFinish[key].map(polyLine2SetCCW);
    const unioned = polygonUnion(loops.map((l) => [l])).map((u) => u[0]);

    const hatch = !projected && isHatchMaterialPaletteSlot[key];

    let hatchMaterial: HatchMaterial | undefined = undefined;
    if (hatch) {
      hatchMaterial = hatchProceduralMaterialNew(HatchMaterialId.Diagonal_Lines);
    }

    if (projected) {
      result.push(
        sheetPolylineGeometryNew({
          handle,
          paths: unioned,
          ...wallExteriorFinishMaskInlineStyle,
        }),
      );
    }

    // TODO: move this hatching to a separate category or combine into a single sheet geometry
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: unioned,
        hatch: hatch,
        hatchMaterial,
      }),
    );
  }

  const mergedCrossSections = mergeCompositeWallLoops(building, levelId, crossSections);

  if (!projected) {
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: mergedCrossSections,
        linewidth: WALL_CUT_STROKE_WIDTH,
        fill: true,
        fillColor: colors.stoneLightX1,
        fillOpacity: percentNew(1),
      }),
    );
  } else if (mergedCrossSections.length > 0) {
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: mergedCrossSections,
        ...wallExteriorFinishMaskInlineStyle,
      }),
    );
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: mergedCrossSections,
        linewidth: WALL_CUT_STROKE_WIDTH,
        fill: true,
        fillColor: colors.stoneLightX3,
        fillOpacity: percentNew(1),
      }),
    );
  }

  return result;
}

/**
 * Computes a set of cut planes that captures every finish layer across all
 * walls on a level. Collects [bottomOffset, topOffset) intervals from all
 * walls, sorts by topOffset, then greedily places cut planes at each uncovered
 * layer's bottomOffset. This preserves the "projected from above" base-outline
 * semantics where openings above the base are excluded from cross-section gaps.
 *
 * An interval [b, t) is covered by a previously placed cut plane P when
 * b <= P < t. Since intervals are sorted by ascending topOffset, any placed
 * cut plane (from an earlier interval) is guaranteed < the current interval's
 * top, so the coverage check reduces to b <= lastCutPlane.
 */
function collectProjectedFinishCutPlanes(
  building: Building,
  levelId: string,
  cutPlaneHeight: LevelInches = levelInchesNew(FLOOR_PLAN_CUT_PLANE),
  reportingUnitRoomIds?: Set<string>,
): Set<LevelInches> {
  const walls = building.walls[levelId].walls;

  interface Interval {
    bottom: LevelInches;
    top: LevelInches;
  }
  const intervals: Interval[] = [];
  const wallPlanesEnabled = !!building.enabledFeatures.wallPlanes;

  for (const wallId in walls) {
    const wall = walls[wallId];
    if (!wallTouchesReportingUnitRooms(wall, reportingUnitRoomIds, wallPlanesEnabled)) {
      continue;
    }
    const wallMass = wall.wallType === WallType.SOLID ? wall.mass : wall;
    const finishLayers = wallMassLayers(wallMass, WallLayerType.FINISH, WallLOD.L2);
    for (const layer of finishLayers) {
      const bottom = layer.section.bottomOffset;
      if (bottom <= cutPlaneHeight) {
        intervals.push({ bottom, top: layer.section.topOffset });
      }
    }
  }

  if (intervals.length === 0) {
    return new Set();
  }

  intervals.sort((a, b) => a.top - b.top);

  const cutPlanes = new Set<LevelInches>();
  let lastCutPlane = -Infinity;

  for (const interval of intervals) {
    if (interval.bottom > lastCutPlane) {
      cutPlanes.add(interval.bottom);
      lastCutPlane = interval.bottom;
    }
  }

  return cutPlanes;
}

function makeWallFinishCrossSectionsProjected(
  building: Building,
  levelId: string,
  cutPlaneHeight?: LevelInches,
  reportingUnitRoomIds?: Set<string>,
) {
  const projectedCutPlanes = collectProjectedFinishCutPlanes(building, levelId, cutPlaneHeight, reportingUnitRoomIds);

  const wallFinishCrossSections: WallSideCrossSection[] = [];
  for (const cutPlane of projectedCutPlanes) {
    wallFinishCrossSections.push(...makeWallFinishCrossSections(building, levelId, cutPlane, reportingUnitRoomIds));
  }

  return wallFinishCrossSections;
}

function makeSheetGeometryForWallCrossSections({
  building,
  levelId,
  cutPlaneHeight,
  wallLayer,
  reportingUnitRoomIds,
}: PlanWallCrossSectionProps) {
  const result: SheetGeometry[] = [];

  const { crossSections, crossSectionObbs } = makeWallCrossSections(
    building,
    levelId,
    wallLayer,
    cutPlaneHeight,
    reportingUnitRoomIds,
  );

  result.push(
    sheetPolylineGeometryNew({
      handle: resolveLevelHandleFromBuilding(building, levelId),
      paths: crossSections,
      relaxationObstacleOBBs: crossSectionObbs,
    }),
  );

  return result;
}

export function makeWallCrossSections(
  building: Building,
  levelId: string,
  wallLayer?: WallLayerType,
  cutPlaneHeight?: LevelInches,
  reportingUnitRoomIds?: Set<string>,
): {
  crossSections: PolyLine2[];
  crossSectionObbs: OBB2[];
} {
  const wallSegments: PolyLine2[] = [];
  const crossSectionObbs: OBB2[] = [];

  let join: PolyLine2[] = [];
  let allJoinsSuccessful = true;

  for (const memberLevelId of resolveLevelIdsWithWalls(building, levelId)) {
    const levelWalls = building.walls[memberLevelId].walls;
    const levelWallSegments: PolyLine2[] = [];
    const wallPlanesEnabled = !!building.enabledFeatures.wallPlanes;

    for (const wallId in levelWalls) {
      const wall = levelWalls[wallId];
      if (!wallTouchesReportingUnitRooms(wall, reportingUnitRoomIds, wallPlanesEnabled)) {
        continue;
      }
      const crossSection = wallCrossSection({
        wall,
        wallLayer,
        cutPlane: cutPlaneHeight,
        roomFloorAssemblies: building.roomFloorAssemblies,
      });

      wallSegments.push(...crossSection);
      levelWallSegments.push(...crossSection);

      let includeExteriorCasing;
      if (wallLayer !== WallLayerType.FINISH) {
        includeExteriorCasing = false;
      }

      if (wall.wallType === WallType.SOLID) {
        const { leftGaps, rightGaps } = buildSolidWallCrossSectionOutlines({
          leftSide: wall.leftSide,
          rightSide: wall.rightSide,
          crossSectionMass: wall.mass,
          cutPlane: cutPlaneHeight,
          includeExteriorCasing,
          roomFloorAssemblies: building.roomFloorAssemblies,

          outlineBuilder: (i, leftPts, rightPts) => {
            const leftSlice = leftPts.slice(i, i + 4);
            const rightSlice = rightPts.slice(i, i + 4);

            const pl = polyLine2New([...leftSlice, ...rightSlice]);
            const obb = makeWallCrossSectionObb(wall, [pl]);
            if (obb) {
              crossSectionObbs.push(obb);
            }
          },
        });

        // if there are no gaps, we can take this fast path
        if (leftGaps.length === 0 && rightGaps.length === 0) {
          const obb = makeWallCrossSectionObb(wall, crossSection);
          if (obb) {
            crossSectionObbs.push(obb);
          }
        }
      }
    }

    const nextJoinLoops = join.concat(levelWallSegments);

    join = joinPolyLine2Loops(nextJoinLoops);

    /**
     * For composite levels, we need to ensure the fallback is triggered if any join fails. Otherwise,
     * there's an issue where an early unsuccessful joinPolyLine2Loops call gets overwritten
     * by subsequent successful joinPolyLine2Loops calls, and no wall data for the earlier levels is
     * included in the output.
     */
    if (nextJoinLoops.length > 0 && join.length === 0) {
      allJoinsSuccessful = false;
    }
  }

  /**
   * Sometimes it is impossible to join wallCrossSections due to wall offsets being
   * disconnected. This will invalidate the whole output from this wallLayer and produce an
   * empty set of walls
   *
   * As a fallback, we return the original wallSegments.
   * While technically not closed polylines (so not fillable),
   * they are renderable linework that will show in their target output without breaking plans.
   *
   * These conditions produce a console alert, and really should be addressed on an individual
   * case-by-case basis
   */
  const crossSections = allJoinsSuccessful
    ? join.filter((x) => {
        return polyLine2Area(x) > ZERO_EPSILON;
      })
    : wallSegments;

  const mergedCrossSections = mergeCompositeWallLoops(building, levelId, crossSections);

  return {
    crossSections: mergedCrossSections,
    crossSectionObbs,
  };
}

function makeWallCrossSectionObb(wall: Wall, crossSection: PolyLine2[]): OBB2 | null {
  const { startX, startY, endX, endY } = getWallPath(wall);

  const path = polyLine2ByTwoPoints(startX, startY, endX, endY);

  return obb2FromPathAndPolyLines(path, crossSection, true);
}
